import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Building2, Users as UsersIcon, DoorClosed, ClipboardCheck, ShieldAlert,
  Search, Loader2, Ban, PlayCircle, UserCog, KeyRound, Crown, Eye,
  CreditCard, Database, Copy, ChevronRight, ExternalLink, CheckSquare, Square,
  Archive, RotateCcw,
} from "lucide-react";
import { platformApi, dataHealthApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtDateTime, apiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MarketplaceTab from "@/pages/platform/MarketplaceTab";
import DiscountsTab from "@/pages/platform/DiscountsTab";

export default function Platform() {
  return (
    <div className="space-y-6">
      <PageHeader title="Super Admin" subtitle="Manage tenants, plans and subscriptions across FD Check" testId="platform-header" />
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="platform-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="companies" data-testid="platform-tab-companies">Companies</TabsTrigger>
          <TabsTrigger value="explorer" data-testid="platform-tab-explorer">Explorer</TabsTrigger>
          <TabsTrigger value="plans" data-testid="platform-tab-plans">Plans</TabsTrigger>
          <TabsTrigger value="marketplace" data-testid="platform-tab-marketplace">Marketplace</TabsTrigger>
          <TabsTrigger value="payments" data-testid="platform-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="discounts" data-testid="platform-tab-discounts">Discounts</TabsTrigger>
          <TabsTrigger value="datahealth" data-testid="platform-tab-datahealth">Data health</TabsTrigger>
          <TabsTrigger value="audit" data-testid="platform-tab-audit">Audit log</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="companies"><CompaniesTab /></TabsContent>
        <TabsContent value="explorer"><ExplorerTab /></TabsContent>
        <TabsContent value="plans"><PlansTab /></TabsContent>
        <TabsContent value="marketplace"><MarketplaceTab /></TabsContent>
        <TabsContent value="payments"><PaymentsTab /></TabsContent>
        <TabsContent value="discounts"><DiscountsTab /></TabsContent>
        <TabsContent value="datahealth"><DataHealthTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Overview ---------------- */
function OverviewTab() {
  const { data, loading, error, reload } = useAsync(() => platformApi.stats(), []);
  if (loading) return <LoadingState label="Loading stats…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const s = data || {};
  const cards = [
    { icon: Building2, label: "Companies", value: s.total_companies },
    { icon: UsersIcon, label: "Users", value: s.total_users },
    { icon: Building2, label: "Buildings", value: s.total_buildings },
    { icon: DoorClosed, label: "Doors", value: s.total_doors },
    { icon: ClipboardCheck, label: "Inspections", value: s.total_inspections },
    { icon: ClipboardCheck, label: "Inspected this month", value: s.inspections_this_month },
    { icon: UsersIcon, label: "Active users (30d)", value: s.active_users_30d },
    { icon: ShieldAlert, label: "Needs admin", value: s.needs_admin_count },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {cards.map((c) => (
          <Card key={c.label} className="p-4" data-testid={`platform-stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-center gap-2 text-slate-400"><c.icon className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wide">{c.label}</span></div>
            <p className="mt-2 text-2xl font-bold text-slate-900">{c.value ?? 0}</p>
          </Card>
        ))}
      </div>
      {s.tier_mix && (
        <Card className="p-5">
          <p className="mb-3 font-semibold text-slate-800">Subscription mix</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(s.tier_mix).map(([tier, n]) => (
              <Badge key={tier} variant="outline" className="text-slate-600">{tier}: <span className="ml-1 font-bold">{n}</span></Badge>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ---------------- Companies ---------------- */
const STATUS_META = {
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700" },
  trial: { label: "Trial", cls: "bg-sky-100 text-sky-700" },
  complimentary: { label: "Complimentary", cls: "bg-violet-100 text-violet-700" },
  free_trial: { label: "Free trial", cls: "bg-amber-100 text-amber-700" },
  expired: { label: "Trial ended", cls: "bg-red-100 text-red-700" },
  suspended: { label: "Suspended", cls: "bg-red-100 text-red-700" },
  archived: { label: "Archived", cls: "bg-slate-200 text-slate-600" },
};

function PlanStatusBadge({ plan }) {
  const meta = STATUS_META[plan?.status] || { label: plan?.status || "—", cls: "text-slate-600" };
  const showDays = (plan?.status === "trial" || plan?.status === "free_trial") && plan?.days_left != null;
  return <Badge className={meta.cls} data-testid={`status-badge-${plan?.status || "none"}`}>{meta.label}{showDays ? ` · ${plan.days_left}d left` : ""}</Badge>;
}

const COMPANY_FILTERS = [
  { v: "active", label: "Active" },
  { v: "paid", label: "Paid" },
  { v: "trial", label: "Trials" },
  { v: "expired", label: "Trial ended" },
  { v: "archived", label: "Archived" },
  { v: "all", label: "All" },
];

function CompaniesTab() {
  const { data, loading, error, reload } = useAsync(() => platformApi.companies({ include_archived: true }), []);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("active");
  const [manageId, setManageId] = useState(null);
  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const counts = useMemo(() => ({
    archived: list.filter((c) => c.is_archived).length,
    active: list.filter((c) => !c.is_archived).length,
  }), [list]);
  const filtered = useMemo(() => {
    let rows = list;
    if (status === "active") rows = rows.filter((c) => !c.is_archived);
    else if (status === "archived") rows = rows.filter((c) => c.is_archived);
    else if (status === "paid") rows = rows.filter((c) => !c.is_archived && c.plan?.status === "paid");
    else if (status === "trial") rows = rows.filter((c) => !c.is_archived && (c.plan?.status === "trial" || c.plan?.status === "free_trial"));
    else if (status === "expired") rows = rows.filter((c) => !c.is_archived && c.plan?.status === "expired");
    const s = q.trim().toLowerCase();
    if (s) rows = rows.filter((c) => `${c.name || ""} ${c.company_code || ""} ${c.postcode || ""}`.toLowerCase().includes(s));
    return rows;
  }, [list, q, status]);

  if (loading) return <LoadingState label="Loading companies…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-1.5">
        {COMPANY_FILTERS.map((f) => (
          <button
            key={f.v}
            onClick={() => setStatus(f.v)}
            data-testid={`company-filter-${f.v}`}
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${status === f.v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            {f.label}{f.v === "archived" && counts.archived ? ` (${counts.archived})` : ""}
          </button>
        ))}
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search companies…" className="pl-9" data-testid="platform-company-search" />
      </div>
      <div className="space-y-2">
        {filtered.map((c) => (
          <Card key={c.id} className={`flex flex-wrap items-center justify-between gap-3 p-4 ${c.is_archived ? "opacity-70" : ""}`} data-testid={`platform-company-${c.id}`}>
            <div className="min-w-0">
              <p className="truncate font-semibold text-slate-900">{c.name}</p>
              <p className="truncate text-sm text-slate-500">{c.company_code} · {c.buildings_count} buildings · {c.doors_count} doors · {c.users_count} users</p>
            </div>
            <div className="flex items-center gap-2">
              <PlanStatusBadge plan={c.plan} />
              <Badge variant="outline" className={c.plan?.at_limit ? "border-amber-300 text-amber-700" : "text-slate-600"}>
                {c.plan?.tier_label || c.plan?.tier} · {c.plan?.max_doors == null ? "∞" : c.plan.max_doors} doors
              </Badge>
              <Button size="sm" variant="outline" onClick={() => setManageId(c.id)} data-testid={`platform-manage-${c.id}`}>Manage</Button>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <EmptyState icon={Building2} title="No companies" message="No tenants match this filter." />}
      </div>
      {manageId && <CompanyManageDialog companyId={manageId} onClose={() => setManageId(null)} onChanged={reload} />}
    </div>
  );
}

function CompanyManageDialog({ companyId, onClose, onChanged }) {
  const { data: c, loading, error, reload } = useAsync(() => platformApi.company(companyId), [companyId]);
  const { beginImpersonation } = useAuth();
  const [busy, setBusy] = useState(false);

  // plan form
  const [tier, setTier] = useState("pro");
  const [unlimited, setUnlimited] = useState(true);
  const [maxDoors, setMaxDoors] = useState(60);
  const [note, setNote] = useState("");
  // trial
  const [trialDays, setTrialDays] = useState(30);
  // seats
  const [seats, setSeats] = useState("");
  const [archiveName, setArchiveName] = useState("");

  React.useEffect(() => {
    if (c) {
      setTier(c.plan?.tier && c.plan.tier !== "basic" ? c.plan.tier : "pro");
      setUnlimited(c.plan?.max_doors == null);
      setMaxDoors(c.plan?.max_doors ?? 60);
      setSeats(c.max_seats ?? "");
    }
  }, [c]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await reload(); onChanged(); }
    catch (e) { toast.error(apiError(e, "Action failed")); }
    finally { setBusy(false); }
  };

  const impersonate = async (u) => {
    try {
      const r = await platformApi.impersonate(u.id);
      await beginImpersonation(r.data.access_token, u.name || u.email);
    } catch (e) { toast.error(apiError(e, "Could not impersonate")); }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.email}:`);
    if (!pw) return;
    run(() => platformApi.resetUserPassword(u.id, { new_password: pw }), "Password reset");
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{c?.name || "Company"}</DialogTitle>
          <DialogDescription>Manage plan, seats, access and users for this tenant.</DialogDescription>
        </DialogHeader>
        {loading && <LoadingState label="Loading…" />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {c && (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge variant="outline">{c.plan?.tier_label || c.plan?.tier}</Badge>
              <Badge variant="outline">{c.plan?.max_doors == null ? "Unlimited" : `${c.plan.max_doors} door`} cap</Badge>
              <Badge variant="outline">{c.counts?.doors} doors used</Badge>
              {c.is_suspended && <Badge className="bg-red-100 text-red-700">Suspended</Badge>}
              {c.is_archived && <Badge className="bg-slate-200 text-slate-600">Archived</Badge>}
            </div>

            {/* Plan override */}
            <section className="space-y-3 rounded-lg border border-slate-100 p-4">
              <p className="font-semibold text-slate-800">Change plan</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tier</Label>
                  <Select value={tier} onValueChange={setTier}>
                    <SelectTrigger data-testid="platform-plan-tier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Basic (FD30)</SelectItem>
                      <SelectItem value="pro">Pro (FD60)</SelectItem>
                      <SelectItem value="pro_maintenance">Pro Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Door limit</Label>
                  <Input type="number" value={unlimited ? "" : maxDoors} disabled={unlimited} onChange={(e) => setMaxDoors(Number(e.target.value))} data-testid="platform-plan-doors" />
                </div>
              </div>
              <div className="flex items-center justify-between rounded bg-slate-50 px-3 py-2">
                <span className="text-sm text-slate-700">Unlimited doors</span>
                <Switch checked={unlimited} onCheckedChange={setUnlimited} data-testid="platform-plan-unlimited" />
              </div>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" />
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} onClick={() => run(() => platformApi.changePlan(companyId, { tier, unlimited_doors: unlimited, max_doors: unlimited ? null : maxDoors, note }), "Plan updated")} data-testid="platform-plan-save">Set plan</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => platformApi.changePlan(companyId, { clear: true }), "Override cleared")} data-testid="platform-plan-clear">Clear override</Button>
              </div>
            </section>

            {/* Trial */}
            <section className="space-y-3 rounded-lg border border-slate-100 p-4">
              <p className="font-semibold text-slate-800">Start a trial</p>
              <div className="flex items-end gap-3">
                <div className="flex-1"><Label>Days</Label><Input type="number" value={trialDays} onChange={(e) => setTrialDays(Number(e.target.value))} data-testid="platform-trial-days" /></div>
                <Button size="sm" disabled={busy} onClick={() => run(() => platformApi.startTrial(companyId, { tier: tier === "basic" ? "pro" : tier, days: trialDays, unlimited_doors: unlimited, max_doors: unlimited ? null : maxDoors }), "Trial started")} data-testid="platform-trial-start">
                  <PlayCircle className="mr-1.5 h-4 w-4" /> Start {tier === "basic" ? "pro" : tier} trial
                </Button>
              </div>
            </section>

            {/* Seat limit + suspend */}
            <section className="space-y-3 rounded-lg border border-slate-100 p-4">
              <p className="font-semibold text-slate-800">Seats & access</p>
              <div className="flex items-end gap-3">
                <div className="flex-1"><Label>Seat limit (blank = unlimited)</Label><Input type="number" value={seats} onChange={(e) => setSeats(e.target.value)} data-testid="platform-seats" /></div>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => platformApi.seatLimit(companyId, { max_seats: seats === "" ? null : Number(seats) }), "Seat limit updated")} data-testid="platform-seats-save">Save seats</Button>
              </div>
              {c.is_suspended ? (
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => platformApi.unsuspend(companyId), "Company unsuspended")} data-testid="platform-unsuspend"><PlayCircle className="mr-1.5 h-4 w-4" /> Unsuspend</Button>
              ) : (
                <Button size="sm" variant="outline" className="text-red-600" disabled={busy} onClick={() => { if (window.confirm("Suspend this company? Users will lose access.")) run(() => platformApi.suspend(companyId, {}), "Company suspended"); }} data-testid="platform-suspend"><Ban className="mr-1.5 h-4 w-4" /> Suspend</Button>
              )}
            </section>

            {/* Danger zone — archive / restore */}
            <section className="space-y-3 rounded-lg border border-red-100 p-4">
              <p className="font-semibold text-red-700">Danger zone</p>
              {c.is_archived ? (
                <>
                  <p className="text-sm text-slate-600">This company is archived — its users cannot sign in and it is hidden from the active list. Data is kept and can be restored at any time{c.archived_at ? ` (archived ${fmtDate(c.archived_at)})` : ""}.</p>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => platformApi.unarchive(companyId), "Company restored")} data-testid="platform-unarchive"><RotateCcw className="mr-1.5 h-4 w-4" /> Restore company</Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-600">Archiving locks every user out and hides the company, but keeps all data so you can restore it later. To confirm, type <span className="font-semibold text-slate-800">{c.name}</span> below.</p>
                  <Input value={archiveName} onChange={(e) => setArchiveName(e.target.value)} placeholder="Type company name to confirm" data-testid="platform-archive-confirm" />
                  <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" disabled={busy || archiveName.trim().toLowerCase() !== (c.name || "").trim().toLowerCase()} onClick={() => run(() => platformApi.archive(companyId, { confirm_name: archiveName }), "Company archived")} data-testid="platform-archive"><Archive className="mr-1.5 h-4 w-4" /> Archive company</Button>
                </>
              )}
            </section>

            {/* Users */}
            <section className="space-y-2 rounded-lg border border-slate-100 p-4">
              <p className="font-semibold text-slate-800">Users ({c.users?.length || 0})</p>
              {(c.users || []).map((u) => (
                <div key={u.id} className="flex items-center justify-between gap-2 border-b border-slate-50 py-2 last:border-0" data-testid={`platform-user-${u.id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-800">{u.name || u.email} {u.is_platform_admin && <Crown className="inline h-3.5 w-3.5 text-amber-500" />}</p>
                    <p className="truncate text-xs text-slate-500">{u.email} · {u.is_company_admin ? "admin" : u.role}</p>
                  </div>
                  {!u.is_platform_admin && u.role !== "client" && (
                    <div className="flex shrink-0 gap-1">
                      {!u.is_company_admin && <Button size="sm" variant="ghost" title="Promote to admin" disabled={busy} onClick={() => run(() => platformApi.promote(u.id), "Promoted to admin")} data-testid={`platform-promote-${u.id}`}><UserCog className="h-4 w-4" /></Button>}
                      <Button size="sm" variant="ghost" title="Reset password" onClick={() => resetPw(u)} data-testid={`platform-reset-${u.id}`}><KeyRound className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" title="Impersonate" onClick={() => impersonate(u)} data-testid={`platform-impersonate-${u.id}`}><Eye className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Plans ---------------- */
function PlansTab() {
  const { data, loading, error, reload } = useAsync(() => platformApi.plans(), []);
  if (loading) return <LoadingState label="Loading plans…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const tiers = data?.defaults?.tiers || {};
  const features = data?.defaults?.features || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {Object.entries(tiers).map(([key, t]) => (
          <Card key={key} className="p-5" data-testid={`platform-plan-${key}`}>
            <p className="text-lg font-bold text-slate-900">{t.label || key}</p>
            {t.tagline && <p className="mt-1 text-sm text-slate-500">{t.tagline}</p>}
            {t.max_doors !== undefined && <p className="mt-3 text-sm text-slate-600">{t.max_doors == null ? "Unlimited doors" : `${t.max_doors} doors`}</p>}
            {t.blurb && <p className="mt-2 text-xs text-slate-400">{t.blurb}</p>}
          </Card>
        ))}
      </div>
      <Card className="p-5">
        <p className="mb-3 font-semibold text-slate-800">Feature gates (lowest tier)</p>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {Object.entries(features).map(([f, tier]) => (
            <div key={f} className="flex items-center justify-between border-b border-slate-50 py-1.5 text-sm">
              <span className="text-slate-600">{f}</span>
              <Badge variant="outline" className="text-slate-500">{tier}</Badge>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-400">Store prices are managed in App Store Connect / Google Play via RevenueCat. Per-company overrides are set under Companies → Manage.</p>
      </Card>
    </div>
  );
}

/* ---------------- Audit ---------------- */
function AuditTab() {
  const { data, loading, error, reload } = useAsync(() => platformApi.audit({ limit: 200 }), []);
  if (loading) return <LoadingState label="Loading audit log…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const rows = Array.isArray(data) ? data : [];
  if (rows.length === 0) return <EmptyState icon={ShieldAlert} title="No audit entries" message="Platform actions will be logged here." />;
  return (
    <Card className="divide-y divide-slate-50">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm" data-testid={`audit-${r.id}`}>
          <div className="min-w-0">
            <p className="truncate font-medium text-slate-800">{r.action}</p>
            <p className="truncate text-xs text-slate-500">{r.actor_email || "system"}{r.metadata?.target_email ? ` → ${r.metadata.target_email}` : ""}</p>
          </div>
          <span className="shrink-0 text-xs text-slate-400">{fmtDateTime(r.created_at)}</span>
        </div>
      ))}
    </Card>
  );
}

/* ---------------- Payments checklist ---------------- */
function IdRow({ label, value, onCopy }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-slate-500">{label}</span>
      <button onClick={onCopy} className="flex items-center gap-1.5 font-mono text-xs font-medium text-slate-700 hover:text-primary" data-testid={`payments-copy-${label.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>{value} <Copy className="h-3 w-3" /></button>
    </div>
  );
}

function PaymentsTab() {
  const { data, loading, error, reload } = useAsync(() => platformApi.paymentsChecklist(), []);
  const [busyKey, setBusyKey] = useState(null);
  const copy = (text) => { try { navigator.clipboard.writeText(text); toast.success("Copied"); } catch { /* clipboard unavailable */ } };
  const toggle = async (key, done) => {
    setBusyKey(key);
    try { await platformApi.togglePaymentsChecklist({ key, done }); await reload(); }
    catch (e) { toast.error(apiError(e, "Could not update")); }
    finally { setBusyKey(null); }
  };
  if (loading) return <LoadingState label="Loading payments checklist…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const d = data || {};
  const total = d.progress?.total || 0;
  const done = d.progress?.done || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const allDone = total > 0 && done === total;
  return (
    <div className="space-y-4">
      <Card className={`p-5 ${allDone ? "border-emerald-200 bg-emerald-50/50" : ""}`} data-testid="payments-progress">
        <div className="flex items-center gap-3">
          <CreditCard className={`h-6 w-6 shrink-0 ${allDone ? "text-emerald-600" : "text-slate-700"}`} />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-900">{allDone ? "Real purchases are live" : `${done} of ${total} steps done`}</p>
            <p className="text-xs text-slate-500">Preview &amp; Expo Go always use the Test Store. Store builds need every step below ticked.</p>
          </div>
          <span className="text-sm font-bold text-slate-700">{pct}%</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
      </Card>

      <Card className="p-5">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Identifiers</p>
        <div className="space-y-1.5 text-sm">
          <IdRow label="iOS bundle id" value={d.ios_bundle_id} onCopy={() => copy(d.ios_bundle_id)} />
          <IdRow label="Android package" value={d.android_package} onCopy={() => copy(d.android_package)} />
          {d.revenuecat_dashboard && (
            <a href={d.revenuecat_dashboard} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 pt-1 text-sm font-medium text-primary hover:underline" data-testid="payments-rc-link"><ExternalLink className="h-3.5 w-3.5" /> Open RevenueCat dashboard</a>
          )}
        </div>
        {Array.isArray(d.products) && d.products.length > 0 && (
          <div className="mt-3 border-t border-slate-100 pt-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Product IDs ({d.products.length}) — tap to copy</p>
            <div className="space-y-0.5">
              {d.products.map((p) => (
                <button key={p.product_id} onClick={() => copy(p.product_id)} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-sm hover:bg-slate-50" data-testid={`payments-product-${p.product_id}`}>
                  <span className="text-slate-600">{p.label} · {p.price}/yr</span>
                  <span className="flex items-center gap-1.5 font-mono text-xs text-slate-400">{p.product_id} <Copy className="h-3 w-3" /></span>
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {(d.sections || []).map((sec) => (
        <Card key={sec.key} className="p-5" data-testid={`payments-section-${sec.key}`}>
          <p className="font-semibold text-slate-800">{sec.title}</p>
          {sec.blurb && <p className="mt-0.5 text-sm text-slate-500">{sec.blurb}</p>}
          <div className="mt-3 space-y-2">
            {sec.items.map((it) => {
              const st = d.state?.[it.key];
              const isDone = !!it.auto || !!st?.done;
              return (
                <div key={it.key} className={`flex items-start gap-3 rounded-lg border p-3 ${isDone ? "border-emerald-100 bg-emerald-50/40" : "border-slate-100"}`} data-testid={`payments-item-${it.key}`}>
                  <button disabled={!!it.auto || busyKey === it.key} onClick={() => toggle(it.key, !isDone)} className="mt-0.5 shrink-0 text-slate-400 disabled:cursor-not-allowed disabled:opacity-70" data-testid={`payments-toggle-${it.key}`}>
                    {busyKey === it.key ? <Loader2 className="h-5 w-5 animate-spin" /> : isDone ? <CheckSquare className={`h-5 w-5 ${it.auto ? "text-slate-400" : "text-emerald-600"}`} /> : <Square className="h-5 w-5" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${isDone ? "text-slate-500" : "text-slate-800"}`}>{it.title}</p>
                    {it.detail && <p className="mt-0.5 text-xs text-slate-500">{it.detail}</p>}
                    {it.url && <a href={it.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">Open guide / console <ExternalLink className="h-3 w-3" /></a>}
                    {it.auto ? <p className="mt-1 text-xs text-slate-400">Done automatically by Emergent</p> : st?.done ? <p className="mt-1 text-xs text-slate-400">Ticked by {st.by || "FD Check"}{st.at ? ` · ${fmtDate(st.at)}` : ""}</p> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ---------------- Data health (backfill) ---------------- */
function DataHealthTab() {
  const [notesRegex, setNotesRegex] = useState("WEEK-ROUTE");
  const [limit, setLimit] = useState("500");
  const [busy, setBusy] = useState(null); // 'dry_run' | 'persist'
  const [result, setResult] = useState(null);

  const parsedLimit = Math.max(1, Math.min(5000, parseInt(limit, 10) || 500));

  const run = async (dryRun) => {
    if (notesRegex.trim()) {
      try { new RegExp(notesRegex.trim()); }
      catch (e) { toast.error(`Invalid regex: ${e.message}`); return; }
    }
    if (!dryRun && !window.confirm(result?.would_update ? `Rewrite ${result.would_update} maintenance job(s)? This is idempotent but cannot be auto-undone.` : "Rewrite historical maintenance jobs with empty items?")) return;
    setBusy(dryRun ? "dry_run" : "persist");
    try {
      const r = await dataHealthApi.backfillEmptyItems({ dry_run: dryRun, notes_regex: notesRegex.trim() || undefined, limit: parsedLimit });
      setResult(r.data);
      if (!dryRun) toast.success(`${r.data.updated} row(s) rewritten`);
    } catch (e) {
      toast.error(apiError(e, "Backfill failed"));
    } finally {
      setBusy(null);
    }
  };

  const canPersist = !!result && result.would_update > 0;
  const stats = result ? [
    { label: "Scanned", value: result.scanned },
    { label: "Would update", value: result.would_update, tone: "text-sky-600" },
    { label: "Updated", value: result.updated, tone: "text-emerald-600" },
    { label: "Skipped: no door", value: result.skipped_no_door, muted: true },
    { label: "Skipped: no inspection", value: result.skipped_no_inspection, muted: true },
    { label: "Skipped: no failed items", value: result.skipped_no_fcp, muted: true },
  ] : [];

  return (
    <div className="max-w-2xl space-y-4">
      <Card className="p-5">
        <div className="flex items-start gap-2">
          <Database className="mt-0.5 h-5 w-5 shrink-0 text-sky-500" />
          <div>
            <p className="font-semibold text-slate-800">Backfill empty-items maintenance jobs</p>
            <p className="mt-1 text-sm text-slate-500">Some batch-created jobs (e.g. the web app’s “Week Route” tool) land with an empty work-required list. This permanently copies the failed-inspection findings into each job. It’s idempotent — safe to re-run — and starts in dry-run so you can preview before writing.</p>
          </div>
        </div>
      </Card>

      <RecomputeDoorStatusesCard />

      <Card className="space-y-3 p-5">
        <p className="font-semibold text-slate-800">Scope</p>
        <div>
          <Label>Notes regex <span className="font-normal text-slate-400">(optional — blank scans every empty-items job)</span></Label>
          <Input value={notesRegex} onChange={(e) => setNotesRegex(e.target.value)} placeholder="WEEK-ROUTE" className="mt-1 font-mono" data-testid="datahealth-notes-regex" />
        </div>
        <div>
          <Label>Row limit <span className="font-normal text-slate-400">(1 – 5000)</span></Label>
          <Input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} className="mt-1" data-testid="datahealth-limit" />
          {limit && parsedLimit !== parseInt(limit, 10) && <p className="mt-1 text-xs text-amber-600">Clamped to {parsedLimit} (allowed 1 – 5000).</p>}
        </div>
        <div className="flex gap-2 pt-1">
          <Button variant="outline" disabled={!!busy} onClick={() => run(true)} data-testid="datahealth-dry-run">
            {busy === "dry_run" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />} Dry run
          </Button>
          <Button disabled={!!busy || !canPersist} onClick={() => run(false)} data-testid="datahealth-persist">
            {busy === "persist" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />} Persist fix
          </Button>
        </div>
        {!result && <p className="text-xs text-slate-500">Run a dry-run first — “Persist fix” unlocks once we’ve confirmed there are rows to correct.</p>}
      </Card>

      {result && (
        <Card className="p-5" data-testid="datahealth-result">
          <p className="mb-3 font-semibold text-slate-800">{result.dry_run ? "Dry-run result" : "Persist result"}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {stats.map((s) => (
              <div key={s.label}>
                <p className={`text-2xl font-bold ${s.tone || (s.muted ? "text-slate-400" : "text-slate-900")}`}>{s.value ?? 0}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
          {Array.isArray(result.sample) && result.sample.length > 0 && (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Sample (first {result.sample.length})</p>
              <div className="space-y-2">
                {result.sample.map((s) => (
                  <div key={s.job_id} className="rounded-lg border border-slate-100 p-2.5 text-sm" data-testid={`datahealth-sample-${s.job_id}`}>
                    <p className="font-medium text-slate-800">{s.title || "(no title)"}</p>
                    <p className="text-xs text-slate-500"><span className="font-semibold text-sky-600">{s.items_added_count}</span> item(s) · first: {s.items_added_first_component || "—"}</p>
                    <p className="mt-0.5 font-mono text-[11px] text-slate-400">job: {String(s.job_id).slice(-8)}{s.inspection_id ? ` · insp: ${String(s.inspection_id).slice(-8)}` : ""}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/* ---------------- Recompute door statuses (backfill) ---------------- */
function RecomputeDoorStatusesCard() {
  const [busy, setBusy] = useState(null); // 'dry_run' | 'persist'
  const [result, setResult] = useState(null);

  const run = async (dryRun) => {
    if (!dryRun && !window.confirm(result?.would_update ? `Recompute the cached status on ${result.would_update} door(s)? This is idempotent and non-destructive.` : "Recompute cached door statuses?")) return;
    setBusy(dryRun ? "dry_run" : "persist");
    try {
      const r = await dataHealthApi.recomputeDoorStatuses({ dry_run: dryRun, limit: 20000 });
      setResult(r.data);
      if (!dryRun) toast.success(`${r.data.updated} door(s) re-synced`);
    } catch (e) {
      toast.error(apiError(e, "Recompute failed"));
    } finally {
      setBusy(null);
    }
  };

  const canPersist = !!result && result.would_update > 0;
  const stats = result ? [
    { label: "Doors scanned", value: result.scanned },
    { label: "Stale (would fix)", value: result.would_update, tone: "text-sky-600" },
    { label: "Re-synced", value: result.updated, tone: "text-emerald-600" },
    { label: "Already in sync", value: result.skipped_in_sync, muted: true },
  ] : [];

  return (
    <>
      <Card className="p-5">
        <div className="flex items-start gap-2">
          <DoorClosed className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
          <div>
            <p className="font-semibold text-slate-800">Recompute door last-inspection statuses</p>
            <p className="mt-1 text-sm text-slate-500">Amending an inspection used to leave a door’s cached “Last: PASS/FAIL” chip stale on the building list (the door detail was correct). This re-derives every door’s chip from its actual latest inspection. Idempotent, non-destructive, and starts in dry-run so you can preview.</p>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" disabled={!!busy} onClick={() => run(true)} data-testid="doorstatus-dry-run">
            {busy === "dry_run" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />} Dry run
          </Button>
          <Button disabled={!!busy || !canPersist} onClick={() => run(false)} data-testid="doorstatus-persist">
            {busy === "persist" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DoorClosed className="mr-2 h-4 w-4" />} Recompute now
          </Button>
        </div>
        {!result && <p className="mt-2 text-xs text-slate-500">Run a dry-run first — “Recompute now” unlocks once we’ve found doors to correct.</p>}
      </Card>

      {result && (
        <Card className="p-5" data-testid="doorstatus-result">
          <p className="mb-3 font-semibold text-slate-800">{result.dry_run ? "Dry-run result" : "Recompute result"}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label}>
                <p className={`text-2xl font-bold ${s.tone || (s.muted ? "text-slate-400" : "text-slate-900")}`}>{s.value ?? 0}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
          {Array.isArray(result.sample) && result.sample.length > 0 && (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Sample ({result.sample.length})</p>
              <div className="space-y-2">
                {result.sample.map((s) => (
                  <div key={s.door_id} className="flex items-center justify-between rounded-lg border border-slate-100 p-2.5 text-sm" data-testid={`doorstatus-sample-${s.door_id}`}>
                    <p className="font-medium text-slate-800">{s.door_reference || "(no ref)"}</p>
                    <p className="text-xs text-slate-500">
                      <span className="font-semibold uppercase text-slate-400">{s.from_status}</span>
                      {" → "}
                      <span className="font-semibold uppercase text-emerald-600">{s.to_status}</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </>
  );
}

/* ---------------- Cross-tenant Explorer ---------------- */
function ExplorerTab() {
  const [stack, setStack] = useState([{ level: "companies", label: "Companies" }]);
  const cur = stack[stack.length - 1];
  const push = (node) => setStack((s) => [...s, node]);
  const goto = (idx) => setStack((s) => s.slice(0, idx + 1));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-1 text-sm" data-testid="explorer-breadcrumb">
        {stack.map((n, i) => (
          <React.Fragment key={i}>
            {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
            <button onClick={() => goto(i)} className={`rounded px-1.5 py-0.5 ${i === stack.length - 1 ? "font-semibold text-slate-800" : "text-primary hover:underline"}`} data-testid={`explorer-crumb-${i}`}>{n.label}</button>
          </React.Fragment>
        ))}
      </div>
      {cur.level === "companies" && <ExplorerCompanies onOpen={(c) => push({ level: "buildings", label: c.name || "Company", companyId: c.id })} />}
      {cur.level === "buildings" && <ExplorerBuildings companyId={cur.companyId} onOpen={(b) => push({ level: "doors", label: b.name || "Building", buildingId: b.id })} />}
      {cur.level === "doors" && <ExplorerDoors buildingId={cur.buildingId} onOpen={(dr) => push({ level: "inspections", label: dr.door_reference || "Door", doorId: dr.id })} />}
      {cur.level === "inspections" && <ExplorerInspections doorId={cur.doorId} />}
    </div>
  );
}

function ExplorerCompanies({ onOpen }) {
  const { data, loading, error, reload } = useAsync(() => platformApi.companies(), []);
  const [q, setQ] = useState("");
  const list = Array.isArray(data) ? data : [];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((c) => `${c.name || ""} ${c.company_code || ""}`.toLowerCase().includes(s));
  }, [list, q]);
  if (loading) return <LoadingState label="Loading companies…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="space-y-3">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search companies…" className="pl-9" data-testid="explorer-company-search" />
      </div>
      <div className="space-y-2">
        {filtered.map((c) => (
          <button key={c.id} onClick={() => onOpen(c)} className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white p-4 text-left hover:border-slate-200" data-testid={`explorer-company-${c.id}`}>
            <div className="min-w-0">
              <p className="truncate font-semibold text-slate-900">{c.name}</p>
              <p className="truncate text-sm text-slate-500">{c.company_code} · {c.buildings_count} buildings · {c.doors_count} doors</p>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
          </button>
        ))}
        {filtered.length === 0 && <EmptyState icon={Building2} title="No companies" message="No tenants match your search." />}
      </div>
    </div>
  );
}

function ExplorerBuildings({ companyId, onOpen }) {
  const { data, loading, error, reload } = useAsync(() => platformApi.companyBuildings(companyId), [companyId]);
  const rows = Array.isArray(data) ? data : [];
  if (loading) return <LoadingState label="Loading buildings…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (rows.length === 0) return <EmptyState icon={Building2} title="No buildings" message="This tenant has no buildings." />;
  return (
    <div className="space-y-2">
      {rows.map((b) => (
        <button key={b.id} onClick={() => onOpen(b)} className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white p-4 text-left hover:border-slate-200" data-testid={`explorer-building-${b.id}`}>
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-900">{b.name}</p>
            <p className="truncate text-sm text-slate-500">{b.address || "—"}{b.postcode ? `, ${b.postcode}` : ""}</p>
            {b.client_name && <p className="truncate text-xs italic text-slate-400">Client: {b.client_name}</p>}
            <p className="mt-1 text-xs text-slate-500">{b.doors_count} doors</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
        </button>
      ))}
    </div>
  );
}

function ExplorerDoors({ buildingId, onOpen }) {
  const { data, loading, error, reload } = useAsync(() => platformApi.buildingDoors(buildingId), [buildingId]);
  const rows = Array.isArray(data) ? data : [];
  if (loading) return <LoadingState label="Loading doors…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (rows.length === 0) return <EmptyState icon={DoorClosed} title="No doors" message="This building has no doors." />;
  return (
    <div className="space-y-2">
      {rows.map((dr) => (
        <button key={dr.id} onClick={() => onOpen(dr)} className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white p-4 text-left hover:border-slate-200" data-testid={`explorer-door-${dr.id}`}>
          <div className="min-w-0">
            <p className="truncate font-mono font-semibold text-slate-900">{dr.door_reference || String(dr.id).slice(-6)}</p>
            <p className="truncate text-sm text-slate-500">{dr.location || "—"}{dr.floor ? ` · Floor ${dr.floor}` : ""}</p>
            <p className="truncate text-xs text-slate-400">{dr.fd_rating || ""} {dr.door_type ? `· ${dr.door_type}` : ""}</p>
            <p className="mt-1 text-xs text-slate-500">{dr.inspections_count} inspections</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
        </button>
      ))}
    </div>
  );
}

function ExplorerInspections({ doorId }) {
  const { data, loading, error, reload } = useAsync(() => platformApi.doorInspections(doorId), [doorId]);
  const rows = Array.isArray(data) ? data : [];
  const tone = (st) => st === "passed" ? "bg-emerald-100 text-emerald-700" : st === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
  if (loading) return <LoadingState label="Loading inspections…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (rows.length === 0) return <EmptyState icon={ClipboardCheck} title="No inspections" message="This door has no inspection records." />;
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <Card key={r.id} className="p-4" data-testid={`explorer-inspection-${r.id}`}>
          <div className="flex items-center gap-2">
            <Badge className={tone(r.status)}>{(r.status || "pending").toUpperCase()}</Badge>
            <span className="text-xs text-slate-400">{fmtDateTime(r.inspection_date)}</span>
          </div>
          <p className="mt-1.5 text-sm font-medium text-slate-800">by {r.inspector_name || "unknown"}</p>
          {r.failed_count > 0 && <p className="mt-0.5 text-xs font-semibold text-red-600">{r.failed_count} failed component{r.failed_count === 1 ? "" : "s"}</p>}
        </Card>
      ))}
    </div>
  );
}

